import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  RestoreFromTrash as TrashIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import GradeService from '../../services/grade.service';

const GradeList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchGrades();
  }, []);

  const fetchGrades = async () => {
    setLoading(true);
    try {
      const response = await GradeService.getAll();
      setGrades(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching grades:', error);
      showSnackbar(t('grades.errorLoadingGrades') + ': ' + (error.response?.data?.message || error.message), 'error');
      setGrades([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await GradeService.delete(deleteDialog.id);
      showSnackbar(t('grades.gradeDeletedSuccess'));
      fetchGrades();
    } catch (error) {
      showSnackbar(t('grades.errorDeletingGrade'), 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredGrades = grades.filter(grade =>
    grade.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('grades.title')}</Typography>
            <Box display="flex" gap={1}>
              {hasPermission('grades.view') && (
                <Button
                  variant="outlined"
                  startIcon={<TrashIcon />}
                  onClick={() => navigate('/grades/deleted')}
                >
                  {t('grades.deletedGrades')}
                </Button>
              )}
              {hasPermission('grades.create') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/grades/new')}
                >
                  {t('grades.newGrade')}
                </Button>
              )}
            </Box>
          </Box>

          <TextField
            placeholder={t('grades.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('grades.name')}</TableCell>
                  <TableCell>{t('grades.createdAt')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredGrades.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {t('grades.noGradesFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredGrades.map((grade) => (
                    <TableRow key={grade.id} hover>
                      <TableCell>{grade.name}</TableCell>
                      <TableCell>{formatDate(grade.created_at)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('grades.edit') && (
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/grades/${grade.id}/edit`)}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {hasPermission('grades.delete') && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(grade.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>{t('grades.deleteGradeTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('grades.deleteGradeConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GradeList;
